import React, { useState, useRef } from 'react';
import { Button } from '../UI/button';
import { Card, CardContent } from '../UI/card';
import { Upload, File, X, AlertCircle, CheckCircle, Download, Loader2 } from 'lucide-react';

/**
 * COMPOSANT: UPLOAD DE FICHIER CSV/EXCEL
 * 
 * Permet d'uploader et prévisualiser un fichier de paiement
 */

const FileUploader = ({
  onFileSelect,
  onFileRemove,
  acceptedFormats = '.csv,.xlsx',
  maxSize = 5 * 1024 * 1024, // 5MB par défaut
  templateUrl = null,
  type = 'third-party', // 'third-party' ou 'bulk'
  loading = false
}) => {
  const [file, setFile] = useState(null);
  const [error, setError] = useState(null);
  const [isDragging, setIsDragging] = useState(false);
  const fileInputRef = useRef(null);

  const validateFile = (selectedFile) => {
    setError(null);

    // Vérifier la taille
    if (selectedFile.size > maxSize) {
      setError(`Le fichier est trop volumineux. Taille maximale: ${maxSize / (1024 * 1024)} MB`);
      return false;
    }

    // Vérifier l'extension
    const fileName = selectedFile.name.toLowerCase();
    const validExtensions = acceptedFormats.split(',').map(ext => ext.trim());
    const hasValidExtension = validExtensions.some(ext => fileName.endsWith(ext));

    if (!hasValidExtension) {
      setError(`Format de fichier non supporté. Formats acceptés: ${acceptedFormats}`);
      return false;
    }

    return true;
  };

  const handleFileSelect = (selectedFile) => {
    if (validateFile(selectedFile)) {
      setFile(selectedFile);
      onFileSelect(selectedFile);
    }
  };

  const handleFileInputChange = (e) => {
    const selectedFile = e.target.files[0];
    if (selectedFile) {
      handleFileSelect(selectedFile);
    }
  };

  const handleRemoveFile = () => {
    setFile(null);
    setError(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
    onFileRemove();
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    setIsDragging(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragging(false);

    const droppedFile = e.dataTransfer.files[0];
    if (droppedFile) {
      handleFileSelect(droppedFile);
    }
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round((bytes / Math.pow(k, i)) * 100) / 100 + ' ' + sizes[i];
  };

  return (
    <div className="space-y-4 relative">
      {loading && (
        <div className="absolute inset-0 z-10 bg-white/80 rounded-lg flex flex-col items-center justify-center gap-3">
          <Loader2 className="w-8 h-8 animate-spin text-primary" />
          <p className="text-sm font-medium text-gray-700">Analyse du fichier en cours...</p>
        </div>
      )}
      {/* Zone de téléchargement du modèle */}
      {templateUrl && type !== 'bulk' && (
        <Card className="bg-blue-50 border-blue-200">
          <CardContent className="p-4">
            <div className="flex items-start justify-between">
              <div className="flex items-start space-x-3">
                <Download className="w-5 h-5 text-blue-600 mt-0.5" />
                <div>
                  <h4 className="font-semibold text-blue-900">
                    Téléchargez d'abord le modèle
                  </h4>
                  <p className="text-sm text-blue-700 mt-1">
                    Utilisez notre modèle pour vous assurer que votre fichier a le bon format
                  </p>
                </div>
              </div>
              <Button
                variant="outline"
                size="sm"
                onClick={() => window.open(templateUrl, '_blank')}
                className="border-blue-300 text-blue-700 hover:bg-blue-100"
              >
                <Download className="w-4 h-4 mr-2" />
                Télécharger
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Zone d'upload */}
      {!file ? (
        <Card
          className={`border-2 border-dashed transition-colors ${
            isDragging
              ? 'border-primary bg-primary/5'
              : 'border-gray-300 hover:border-primary/50'
          } ${error ? 'border-red-500' : ''}`}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
        >
          <CardContent className="p-8 text-center">
            <Upload className={`w-12 h-12 mx-auto mb-4 ${isDragging ? 'text-primary' : 'text-gray-400'}`} />
            <h3 className="text-lg font-semibold mb-2">
              {isDragging ? 'Déposez le fichier ici' : 'Sélectionnez un fichier'}
            </h3>
            <p className="text-sm text-muted-foreground mb-4">
              ou glissez-déposez votre fichier ici
            </p>
            <Button
              type="button"
              variant="outline"
              onClick={() => fileInputRef.current?.click()}
            >
              Parcourir les fichiers
            </Button>
            <input
              ref={fileInputRef}
              type="file"
              accept={acceptedFormats}
              onChange={handleFileInputChange}
              className="hidden"
            />
            <p className="text-xs text-muted-foreground mt-4">
              Formats acceptés: {acceptedFormats} • Taille max: {maxSize / (1024 * 1024)} MB
            </p>
            
            {error && (
              <div className="mt-4 flex items-center justify-center text-red-600 text-sm">
                <AlertCircle className="w-4 h-4 mr-2" />
                {error}
              </div>
            )}
          </CardContent>
        </Card>
      ) : (
        <Card className="border-green-200 bg-green-50">
          <CardContent className="p-4">
            <div className="flex items-start justify-between">
              <div className="flex items-start space-x-3">
                <div className="p-2 bg-green-100 rounded">
                  <File className="w-5 h-5 text-green-600" />
                </div>
                <div className="flex-1">
                  <div className="flex items-center space-x-2">
                    <CheckCircle className="w-4 h-4 text-green-600" />
                    <h4 className="font-semibold text-green-900">
                      Fichier sélectionné
                    </h4>
                  </div>
                  <p className="text-sm text-green-700 mt-1">{file.name}</p>
                  <p className="text-xs text-green-600 mt-1">
                    {formatFileSize(file.size)}
                  </p>
                </div>
              </div>
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={handleRemoveFile}
                className="text-red-600 hover:text-red-700 hover:bg-red-50"
              >
                <X className="w-4 h-4" />
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Instructions */}
      {type !== 'bulk' && <Card className="bg-gray-50">
        <CardContent className="p-4">
          <h4 className="font-semibold text-sm mb-2">📋 Instructions</h4>
          <ul className="text-xs text-muted-foreground space-y-1">
            {type === 'third-party' ? (
              <>
                <li>• Téléchargez le modèle de fichier fourni</li>
                <li>• Remplissez toutes les colonnes obligatoires</li>
                <li>• Vérifiez que les matricules existent</li>
                <li>• Assurez-vous que les montants sont corrects</li>
                <li>• Supprimez les lignes d'exemple avant l'import</li>
              </>
            ) : (
              <>
                <li>• Téléchargez le modèle spécifique à votre entreprise</li>
                <li>• Remplissez les statuts de paiement (PAYE/IMPAYE/PARTIEL)</li>
                <li>• Indiquez les montants payés pour chaque employé</li>
                <li>• Ajoutez la référence de virement ou chèque</li>
                <li>• Maximum 500 lignes par fichier</li>
              </>
            )}
          </ul>
        </CardContent>
      </Card>}
    </div>
  );
};

export default FileUploader;
